/**
 * Renomear uma automação: o nome e a descrição.
 *
 * Não cria versão nem mexe no que está no ar (`PATCH .../nome-e-descricao`).
 * Quem renomeia é quem monta aquele tipo de automação, a mesma régua de editar.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chaves } from '@/shared/api/chaves';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoArea, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import type { Automacao } from '../lib/automacoes';
import './configurar-modelo.css';

export function RenomearAutomacao({
  automacao,
  aoFechar,
  aoSalvo,
}: {
  automacao: Automacao;
  aoFechar: () => void;
  aoSalvo: (nome: string) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [nome, definirNome] = useState(automacao.nome ?? '');
  const [descricao, definirDescricao] = useState(automacao.descricao ?? '');
  const [tentou, definirTentou] = useState(false);
  const mudou =
    nome.trim() !== (automacao.nome ?? '') || descricao.trim() !== (automacao.descricao ?? '');

  const salvar = useMutation({
    mutationFn: () =>
      api!.identificarAutomacao({
        caminho: { id: automacao.id },
        corpo: { nome: nome.trim(), descricao: descricao.trim() },
      }),
    onSuccess: (salva) => {
      cache.setQueryData(chaves.automacao(automacao.id), salva);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      aoSalvo(salva.nome ?? nome.trim());
    },
  });

  const erroNome = tentou && !nome.trim() ? 'Dê um nome para a automação.' : undefined;

  const enviar = (e: FormEvent): void => {
    e.preventDefault();
    definirTentou(true);
    if (!nome.trim() || !mudou || salvar.isPending) return;
    salvar.mutate();
  };

  return (
    <Dialogo
      titulo="Renomear"
      forma="lateral"
      aoFechar={() => {
        if (!salvar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={salvar.isPending}
            motivo="Salvando…"
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="renomear-automacao"
            variante="primario"
            desabilitado={!mudou || salvar.isPending || api === null}
            motivo={
              salvar.isPending ? 'Salvando…' : !mudou ? 'Nada mudou.' : 'Sem sessão: entre de novo.'
            }
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
        </>
      }
    >
      <form id="renomear-automacao" className="configurar-modelo" onSubmit={enviar} noValidate>
        <p className="configurar-modelo__regra">
          Mudar o nome não cria versão nova nem mexe no que está no ar.
        </p>
        <Campo
          rotulo="Nome"
          maxLength={120}
          value={nome}
          onChange={(e) => definirNome(e.target.value)}
          {...(erroNome ? { erro: erroNome } : {})}
        />
        <CampoArea
          rotulo="Descrição (opcional)"
          rows={3}
          maxLength={500}
          value={descricao}
          onChange={(e) => definirDescricao(e.target.value)}
        />
        {salvar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(salvar.error, {
              generica: 'Não foi possível salvar agora. Nada mudou; tente de novo.',
              porCodigo: {
                ENGINE_INDISPONIVEL:
                  'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
              },
            })}
          </Faixa>
        )}
      </form>
    </Dialogo>
  );
}
